import React from "react";
import { gql } from "@apollo/client";
import {
  useCanImpersonate,
  startImpersonating,
} from "@src/features/app/hooks/useImpersonation";
import { useDebouncedInputValue } from "@src/lib/hooks/useDebouncedInputValue";
import Text from "@src/lib/ui/Text";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@src/lib/ui/Accordion";
import { Tooltip, TooltipContent, TooltipTrigger } from "@src/lib/ui/Tooltip";
import {
  SearchUsersForImpersonationQuery,
  useSearchUsersForImpersonationQuery,
} from "./ImpersonateUser.generated";

type Employee = SearchUsersForImpersonationQuery["People"]["objects"][number];

function formatEmployeeLabel(emp: Employee): string {
  const parts = [
    `${emp.firstName} ${emp.lastName} (${emp.id})`,
    emp.userType,
    emp.locations.map((l) => l.name).join(", "),
    emp.roles.map((r) => r.name).join(", "),
  ].filter(Boolean);
  return parts.join(", ");
}

const PAGE_SIZE = 20;

const ImpersonateUser: React.FC = () => {
  const canImpersonate = useCanImpersonate();
  if (!canImpersonate) return null;
  return <ImpersonateUserAccordion />;
};

const ImpersonateUserAccordion: React.FC = () => {
  const {
    value: search,
    setValue: setSearch,
    debouncedValue: debouncedSearch,
  } = useDebouncedInputValue({ initialValue: "", delay: 300 });

  const { data, loading } = useSearchUsersForImpersonationQuery({
    variables: {
      input: {
        ...(debouncedSearch ? { search: { value: debouncedSearch } } : {}),
      },
      pagination: { limit: PAGE_SIZE },
    },
  });

  const employees: Employee[] = data?.People?.objects || [];

  return (
    <Accordion type="single" collapsible className="w-full">
      <AccordionItem value="impersonation">
        <AccordionTrigger>
          <Text variant="body-title">Impersonate user</Text>
        </AccordionTrigger>
        <AccordionContent>
          <div className="flex flex-col gap-2">
            <input
              type="text"
              placeholder="Search by name, email, or phone..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full rounded border border-gray-6 px-2 py-1.5 text-sm outline-none focus:border-blue-7"
            />
            {loading && (
              <Text variant="fine-print-description" className="text-gray-8">
                Loading...
              </Text>
            )}
            <div className="flex flex-col">
              {employees.map((emp) => {
                const label = formatEmployeeLabel(emp);
                return (
                  <Tooltip key={emp.id}>
                    <TooltipTrigger asChild>
                      <button
                        className="cursor-pointer truncate rounded p-1 text-left text-sm hover:bg-blue-2"
                        onClick={() =>
                          startImpersonating({
                            id: emp.id,
                            name: `${emp.firstName} ${emp.lastName}`,
                            userType: emp.userType,
                          })
                        }
                      >
                        {label}
                      </button>
                    </TooltipTrigger>
                    <TooltipContent side="right">{label}</TooltipContent>
                  </Tooltip>
                );
              })}
              {!loading && employees.length === 0 && (
                <Text variant="fine-print-description" className="text-gray-8">
                  No users found
                </Text>
              )}
            </div>
          </div>
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  );
};

gql`
  query SearchUsersForImpersonation(
    $input: GetPeopleInput!
    $pagination: PaginationInput
  ) {
    People(input: $input, pagination: $pagination) {
      totalCount
      objects {
        id
        firstName
        lastName
        userType
        email
        phoneNumber
        locations {
          id
          name
        }
        roles {
          id
          name
        }
      }
    }
  }
`;

export default ImpersonateUser;
